import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Typography, Box, useTheme, useMediaQuery, AppBar, Toolbar } from '@mui/material';
import { useSearchParams } from 'react-router';
import AppTopBar from '../../app-bar/AppBar';
import Footer from '../../footer/Footer';
import FilterDropdown from '../../filter-dropdown/FilterDropDown';
import { QlikVizPanel } from '../../qlik-viz-panel/QlikVizPanel';
import { PAGES } from '../../../constants';
import { ExtraControlsConfig } from '../../../types';
import { INDUSTRY_FIELD, CLASS_DESCRIPTION_FIELD, COMPANY_ANNUAL_REV_FIELD, REGION_FIELD, QLIK_APP_REF } from '../../../constants/qlik';
import { applySelectMatch, applySelectPossible } from '../../../services/qlikSelectionService';

const PeerPurchasing: React.FC = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [searchParams] = useSearchParams();
	const page = useMemo(() => PAGES['peer-purchasing-insights'].contents ?? {}, []);

	const [initialized, setInitialized] = useState(false);
	const [pageTitle] = useState(page.title?.text || '');
	const [pageSubtitle] = useState(page.sub?.text || '');

	// Initialize selections from URL params (mirrors Angular controller logic)
	useEffect(() => {
		initFromUrlParams();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const initFromUrlParams = async () => {
		try {
			const indClass = searchParams.get('indClass');
			const annualRevParam = searchParams.get('annualRev');
			const region = searchParams.get('region');

			// If industry class passed, select it then open possible industries
			if (indClass) {
				await applySelectMatch(QLIK_APP_REF, CLASS_DESCRIPTION_FIELD, indClass, '', window.location.pathname);
				await applySelectPossible(QLIK_APP_REF, INDUSTRY_FIELD);
			}

			// Map annual revenue value to bucket and select
			if (annualRevParam) {
				const annualRev = parseInt(annualRevParam, 10);
				let bucket: string | null = null;
				if (annualRev > 500000000) bucket = 'Over $501M';
				else if (annualRev > 150000000) bucket = '$151M to $500M';
				else if (annualRev > 25000000) bucket = '$25.1M to $150M';
				else if (annualRev > 0) bucket = 'Under $25M';
				if (bucket) {
					await applySelectMatch(QLIK_APP_REF, COMPANY_ANNUAL_REV_FIELD, bucket, '', window.location.pathname);
				}
			}

			if (region) {
				await applySelectMatch(QLIK_APP_REF, REGION_FIELD, region, '', window.location.pathname);
			}

			setInitialized(true);
		} catch (error) {
			console.error('Error initializing PeerPurchasing from URL params:', error);
			setInitialized(true);
		}
	};

	// Filter elements for AppTopBar (desktop will also optionally show sticky bar similar to CostsBreakdown if needed later)
	const filterElements = (
		<>
			<FilterDropdown
				formName="Page Filters"
				fieldName={INDUSTRY_FIELD}
				fieldLabel="Industry"
				multiSelect={false}
				qlikSort="qSortByAscii"
			/>
			<FilterDropdown
				formName="Page Filters"
				fieldName={COMPANY_ANNUAL_REV_FIELD}
				fieldLabel="Company Revenue"
				multiSelect={true}
				qlikSort="qSortByLoadOrder"
				sortExpression="SortValue"
			/>
			<FilterDropdown
				formName="Page Filters"
				fieldName={REGION_FIELD}
				fieldLabel="Region"
				multiSelect={false}
				qlikSort="qSortByLoadOrder"
			/>
		</>
	);

	const extraControls: ExtraControlsConfig = {
		startOver: { enable: true },
		lastUpdate: { enable: true, componentLocation: 'nav' },
	};

	return (
		<>
			<AppTopBar
				extraControls={extraControls}
				filterElements={filterElements}
			/>
            {!isMobile && (
                <AppBar
                position="sticky"
                sx={{
                    top: 65, // Height of main AppBar
                    zIndex: (theme) => theme.zIndex.appBar + 1,
                    backgroundColor: 'white',
                    boxShadow: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    ml: '57px', // Align with collapsed drawer
                    width: 'calc(100% - 57px)',
                }}
                >
                    <Toolbar
                        sx={{
                        pt: 2,
                        minHeight: '50px !important',
                        }}
                    >
                        <Grid container spacing={2} alignItems="center" sx={{ flexGrow: 1 }}>
                        {/* Left side - Industry filter */}
                        <Grid size={{ md: 3}}>
                            <FilterDropdown
                                formName="Page Filters"
                                fieldName={INDUSTRY_FIELD}
                                fieldLabel="Industry"
                                multiSelect={false}
                                qlikSort="qSortByAscii"
                            />
                        </Grid>

                        {/* Right side - Company Revenue filter */}
                        <Grid size={{ md: 3}} offset={{ md: 'auto'}}>
                            <FilterDropdown
                                formName="Page Filters"
                                fieldName={COMPANY_ANNUAL_REV_FIELD}
                                fieldLabel="Company Revenue"
                                multiSelect={true}
                                qlikSort="qSortByLoadOrder"
                                sortExpression="SortValue"
                            />
                        </Grid>

                        {/* Right side - Date Range filter */}
                        <Grid size={{ md: 3}} offset={{ md: 0}}>
                            <FilterDropdown
                                formName="Page Filters"
                                fieldName={REGION_FIELD}
                                fieldLabel="Region"
                                multiSelect={false}
                                qlikSort="qSortByLoadOrder"
                            />
                        </Grid>
                        </Grid>
                    </Toolbar>
                </AppBar>
            )}

			{/* Title Section */}
			<Grid
				container
				component="main"
				spacing={0}
				sx={{
					py: 2,
					px: { xs: 2, sm: 2, md: 2 },
					ml: { xs: 0, md: '57px' },
					transition: 'margin-left 0.3s'
				}}
			>
				<Grid size={{ xs: 12 }}>
					{/* Mobile Title */}
					<Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 2 }}>
						<Typography variant="h5" sx={{ mb: 1, fontWeight: 400 }}>
							{pageTitle}
						</Typography>
						<Box sx={{ borderTop: 3, borderColor: 'primary.main', width: 60, mx: 'auto', mb: 2 }} />
					</Box>

					{/* Desktop Title */}
					<Box sx={{ display: { xs: 'none', md: 'block' }, mb: 3 }}>
						<Typography variant="h5" sx={{ mb: 2, fontWeight: 400 }}>
							{pageTitle}
						</Typography>
						<Box sx={{ borderLeft: 3, borderColor: 'primary.main', pl: 2 }}>
							<Typography variant="body1">{pageSubtitle}</Typography>
						</Box>
					</Box>
				</Grid>

				{/* Charts Row */}
				<Grid size={{ xs: 12 }}>
					<Grid container spacing={3}>
						<Grid size={{ xs: 12, md: 6 }}>
							<QlikVizPanel
								qlikVizName="peer-purch-inforce-prim-lim-dist"
								enableInteractions={false}
								equalHeight={true}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<QlikVizPanel
								qlikVizName="peer-purch-inforce-deduc-dist"
								enableInteractions={false}
								equalHeight={true}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<Footer componentLocation="dashboard" />
		</>
	);
};

export default PeerPurchasing;
